import React, { useEffect, useState } from "react";
import "./styles/dashboard.css";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import CommonHeader from "./components/CommonHeader";

const Agencies = () => {
  const navigate = useNavigate();

  const [agencies, setAgencies] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [whatsappChatList, setWhatsappChatList] = useState([]);
  const [refreshingChatList, setRefreshingChatList] = useState(false);
  const [agenciesForMerge, setAgenciesForMerge] = useState([]);
  const [filters, setFilters] = useState({
    agency_name: '',
    status: ''
  });

  // Form state for add/edit
  const [formData, setFormData] = useState({
    agency_id: '',
    agency_name: '',
    status: '1',
    agent_whatsapp_chat_id: ''
  });

  // Merge form state
  const [mergeFormData, setMergeFormData] = useState({
    source_agency_id: '',
    target_agency_id: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMergeFormChange = (e) => {
    const { name, value } = e.target;
    setMergeFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      agency_id: '',
      agency_name: '',
      status: '1',
      agent_whatsapp_chat_id: ''
    });
  };

  const resetMergeForm = () => {
    setMergeFormData({
      source_agency_id: '',
      target_agency_id: ''
    });
  };

  // Fetch WhatsApp chat list
  const fetchWhatsappChatList = () => {
    fetch(`${process.env.REACT_APP_API_URL}/suggests/getwhatsappchatlist`, {
      method: "post",
      headers: {
        Authorization: localStorage.getItem("token"),
      },
    }).then(async (res) => {
      let resp = await res.json();
      console.log('WhatsApp chat list response:', resp);

      if (resp.status === 1) {
        setWhatsappChatList(resp.list || []);
      } else {
        toast.error(resp.message || 'Failed to fetch WhatsApp chat list');
        setWhatsappChatList([]);
      }
    }).catch(error => {
      console.error('Error fetching WhatsApp chat list:', error);
      toast.error('Failed to fetch WhatsApp chat list');
      setWhatsappChatList([]);
    });
  };

  // Refresh WhatsApp chat list
  const refreshWhatsappChatList = () => {
    setRefreshingChatList(true);
    fetch(`${process.env.REACT_APP_API_URL}/Chats_Ids_Fetching/refresh`, {
      method: "post",
      headers: {
        Authorization: localStorage.getItem("token"),
      },
    }).then(async (res) => {
      let resp = await res.json();
      console.log('Refresh chat list response:', resp);

      if (resp.status === 1) {
        toast.success('WhatsApp chat list refreshed successfully');
        // Fetch the updated list
        fetchWhatsappChatList();
      } else {
        toast.error(resp.message || 'Failed to refresh WhatsApp chat list');
      }
      setRefreshingChatList(false);
    }).catch(error => {
      console.error('Error refreshing WhatsApp chat list:', error);
      toast.error('Failed to refresh WhatsApp chat list');
      setRefreshingChatList(false);
    });
  };

  // Fetch agencies for merge (excluding the selected source agency)
  const fetchAgenciesForMerge = (excludeAgencyId) => {
    const data = new URLSearchParams();
    data.append('exclude_agency_id', excludeAgencyId);

    fetch(`${process.env.REACT_APP_API_URL}/agencies/getAgenciesForMerge`, {
      method: "post",
      headers: {
        Authorization: localStorage.getItem("token"),
      },
      body: data,
    }).then(async (res) => {
      let resp = await res.json();
      console.log('Agencies for merge response:', resp);

      if (resp.status === 1) {
        setAgenciesForMerge(resp.list || []);
      } else {
        toast.error(resp.message || 'Failed to fetch agencies for merge');
        setAgenciesForMerge([]);
      }
    }).catch(error => {
      console.error('Error fetching agencies for merge:', error);
      toast.error('Failed to fetch agencies for merge');
      setAgenciesForMerge([]);
    });
  };

  // Handle merge agency
  const handleMergeAgencies = () => {
    if (!mergeFormData.source_agency_id || !mergeFormData.target_agency_id) {
      toast.error('Please select both source and target agencies');
      return;
    }

    if (mergeFormData.source_agency_id === mergeFormData.target_agency_id) {
      toast.error('Source and target agencies cannot be the same');
      return;
    }

    if (window.confirm('Are you sure you want to merge these agencies? This action cannot be undone.')) {
      setMergeLoading(true);
      const data = new URLSearchParams();
      data.append('source_agency_id', mergeFormData.source_agency_id);
      data.append('target_agency_id', mergeFormData.target_agency_id);

      fetch(`${process.env.REACT_APP_API_URL}/agencies/mergeAgencies`, {
        method: "post",
        headers: {
          Authorization: localStorage.getItem("token"),
        },
        body: data,
      }).then(async (res) => {
        let resp = await res.json();
        console.log('Merge response:', resp);

        if (resp.status === 1) {
          toast.success(`Agencies merged successfully. ${resp.bookings_updated} bookings updated.`);
          setShowMergeModal(false);
          resetMergeForm();
          fetchAgencies();
        } else {
          toast.error(resp.message || 'Failed to merge agencies');
        }
        setMergeLoading(false);
      }).catch(error => {
        console.error('Error merging agencies:', error);
        toast.error('Failed to merge agencies');
        setMergeLoading(false);
      });
    }
  };

  // Handle merge click
  const handleMergeClick = (agency) => {
    setSelectedAgency(agency);
    setMergeFormData({
      source_agency_id: agency.agency_id,
      target_agency_id: ''
    });
    fetchAgenciesForMerge(agency.agency_id);
    setShowMergeModal(true);
  };

  const handleAddAgency = () => {
    if (!formData.agency_name.trim()) {
      toast.error('Agency name is required');
      return;
    }

    setLoading(true);
    const data = new URLSearchParams();
    data.append('agency_name', formData.agency_name);
    data.append('status', formData.status);
    data.append('agent_whatsapp_chat_id', formData.agent_whatsapp_chat_id);

    fetch(`${process.env.REACT_APP_API_URL}/agencies/addagency`, {
      method: "post",
      headers: {
        Authorization: localStorage.getItem("token"),
      },
      body: data,
    }).then(async (res) => {
      let resp = await res.json();
      console.log(resp);

      if (resp.status === 1 || resp.message === "Added" || resp.message === "Updated") {
        toast.success("Agency saved successfully");
        setShowAddModal(false);
        resetForm();
        fetchAgencies();
      } else {
        toast.error(resp.message || 'Failed to save agency');
      }
      setLoading(false);
    }).catch(error => {
      console.error('Error saving agency:', error);
      toast.error('Failed to save agency');
      setLoading(false);
    });
  };

  const handleEditAgency = () => {
    if (!formData.agency_name.trim()) {
      toast.error('Agency name is required');
      return;
    }

    setLoading(true);
    const data = new URLSearchParams();
    data.append('agency_id', formData.agency_id);
    data.append('agency_name', formData.agency_name);
    data.append('status', formData.status);
    data.append('agent_whatsapp_chat_id', formData.agent_whatsapp_chat_id);

    fetch(`${process.env.REACT_APP_API_URL}/agencies/addagency`, {
      method: "post",
      headers: {
        Authorization: localStorage.getItem("token"),
      },
      body: data,
    }).then(async (res) => {
      let resp = await res.json();
      console.log(resp);

      if (resp.status === 1 || resp.message === "Updated") {
        toast.success("Agency updated successfully");
        setShowEditModal(false);
        resetForm();
        fetchAgencies();
      } else {
        toast.error(resp.message || 'Failed to update agency');
      }
      setLoading(false);
    }).catch(error => {
      console.error('Error updating agency:', error);
      toast.error('Failed to update agency');
      setLoading(false);
    });
  };

  const handleDeleteAgency = (agencyId, agencyName) => {
    if (window.confirm(`Are you sure you want to delete agency "${agencyName}"?`) === false) {
      return false;
    }

    const data = new URLSearchParams();
    data.append('agency_id', agencyId);
    
    fetch(`${process.env.REACT_APP_API_URL}/agencies/deleteagency`, {
      method: "post",
      headers: {
        Authorization: localStorage.getItem("token"),
      },
      body: data,
    }).then(async (res) => {
      let resp = await res.json();
      console.log(resp);

      if (resp.status === 1 || resp.message === "Deleted") {
        toast.success("Agency deleted successfully");
        fetchAgencies();
      } else {
        toast.error(resp.message || 'Failed to delete agency');
      }
    }).catch(error => {
      console.error('Error deleting agency:', error);
      toast.error('Failed to delete agency');
    });
  };

  const handleEditClick = (agency) => {
    setFormData({
      agency_id: agency.agency_id,
      agency_name: agency.agency_name,
      status: agency.status,
      agent_whatsapp_chat_id: agency.agent_whatsapp_chat_id || ''
    });
    setShowEditModal(true);
  };

  const fetchAgencies = () => {
    setLoading(true);
    const data = new URLSearchParams();
    if (filters.agency_name) data.append('agency_name', filters.agency_name);
    if (filters.status) data.append('status', filters.status);

    fetch(`${process.env.REACT_APP_API_URL}/agencies/getlist`, {
      method: "post",
      headers: {
        Authorization: localStorage.getItem("token"),
      },
      body: data,
    }).then(async (res) => {
      let resp = await res.json();
      //console.log(resp.list);

      if (resp.status === 1) {
        setAgencies(resp.list || []);
      } else {
        toast.error(resp.message || 'Failed to fetch agencies');
        setAgencies([]);
      }
      setLoading(false);
    }).catch(error => {
      console.error('Error fetching agencies:', error);
      toast.error('Failed to fetch agencies');
      setAgencies([]);
      setLoading(false);
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchAgencies();
  };

  const clearFilters = () => {
    setFilters({
      agency_name: '',
      status: ''
    });
  };

  useEffect(() => {
    fetchAgencies();
    fetchWhatsappChatList(); // Fetch chat list on component mount
  }, []);

  return (
    <>
      <div class="d-flex flex-column flex-lg-row h-lg-full bg-surface-secondary">
        <div class="h-screen flex-grow-1 overflow-y-lg-auto">
          <CommonHeader />

          <main class="py-6 bg-surface-secondary">
            <div class="container-fluid">
              <div class="card shadow border-0 mb-7">
                <div class="card-header">
                  <h5 class="mb-0">Agencies</h5>
                </div>

                {/* Filters */}
                <div class="card-body border-bottom">
                  <form onSubmit={handleFilterSubmit} class="row g-3">
                    <div class="col-md-4">
                      <label class="form-label">Agency Name</label>
                      <input
                        type="text"
                        class="form-control"
                        name="agency_name"
                        value={filters.agency_name}
                        onChange={handleFilterChange}
                        placeholder="Search by agency name..."
                      />
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Status</label>
                      <select
                        class="form-select"
                        name="status"
                        value={filters.status}
                        onChange={handleFilterChange}
                      >
                        <option value="">All Status</option>
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                      </select>
                    </div>
                    <div class="col-md-3 d-flex align-items-end">
                      <button type="submit" class="btn btn-primary me-2">
                        <i class="bi bi-search me-1"></i>
                        Search
                      </button>
                      <button type="button" class="btn btn-outline-secondary" onClick={clearFilters}>
                        Clear
                      </button>
                    </div>
                  </form>
                </div>

                <div class="table-responsive">
                  <table class="table table-hover table-nowrap">
                    <thead class="thead-light">
                      <tr>
                        <th scope="col">Agency Name</th>
                        <th scope="col">WhatsApp Chat ID</th>
                        <th scope="col">Status</th>
                        <th scope="col">Created On</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colspan="5" class="text-center">
                            <div class="spinner-border" role="status">
                              <span class="visually-hidden">Loading...</span>
                            </div>
                          </td>
                        </tr>
                      ) : agencies.length > 0 ? (
                        agencies.map((agency) => (
                          <tr key={agency.agency_id}>
                            <td>{agency.agency_name}</td>
                            <td>
                              {agency.agent_whatsapp_chat_id ? (
                                <span class="badge bg-info">{agency.chat_name}</span>
                              ) : (
                                <span class="text-muted">Not set</span>
                              )}
                            </td>
                            <td>
                              <span class="badge badge-lg badge-dot">
                                {agency.status === '0' ? (
                                  <>
                                    <i style={{ backgroundColor: "gray" }}></i>
                                    {'Inactive'}
                                  </>
                                ) : (
                                  <>
                                    <i className="bg-success"></i>
                                    {'Active'}
                                  </>
                                )}
                              </span>
                            </td>
                            <td>{new Date(agency.created_on).toLocaleDateString()}</td>
                            <td class="text-end">
                              <button
                                onClick={() => handleEditClick(agency)}
                                class="btn btn-sm btn-neutral"
                                style={{ marginRight: "0.5rem" }}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleMergeClick(agency)}
                                class="btn btn-sm btn-warning"
                                style={{ marginRight: "0.5rem" }}
                                title="Merge Agency"
                              >
                                Merge
                              </button>
                              <button
                                type="button"
                                class="btn btn-sm btn-square btn-neutral text-danger-hover"
                                onClick={() => handleDeleteAgency(agency.agency_id, agency.agency_name)}
                                title="Delete Agency"
                              >
                                <i class="bi bi-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colspan="5" class="text-center text-muted">
                            No agencies found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div class="card-footer border-0 py-5">
                  <button
                    class="btn d-inline-flex btn-sm btn-primary mx-1"
                    onClick={() => setShowAddModal(true)}
                  >
                    <span class="pe-2">
                      <i class="bi bi-plus"></i>
                    </span>
                    <span>Add Agency</span>
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Add Agency Modal */}
      {showAddModal && (
        <div class="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div class="modal-dialog">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">Add New Agency</h5>
                <button 
                  type="button" 
                  class="btn-close" 
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                ></button>
              </div>
              <div class="modal-body">
                <form>
                  <div class="mb-3">
                    <label class="form-label">Agency Name *</label>
                    <input
                      type="text"
                      class="form-control"
                      name="agency_name"
                      value={formData.agency_name}
                      onChange={handleInputChange}
                      placeholder="Enter agency name"
                      required
                    />
                  </div>
                  <div class="mb-3">
                    <label class="form-label">Status</label>
                    <select
                      class="form-select"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </select>
                  </div>
                  <div class="mb-3">
                    <label class="form-label">WhatsApp Chat ID</label>
                    <div class="d-flex align-items-center">
                      <select
                        class="form-select"
                        name="agent_whatsapp_chat_id"
                        value={formData.agent_whatsapp_chat_id}
                        onChange={handleInputChange}
                      >
                        <option value="">Select a WhatsApp Chat ID</option>
                        {whatsappChatList.map((chat) => (
                          <option key={chat.chat_id} value={chat.chat_id}>
                            {chat.chat_name} ({chat.chat_id})
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-secondary ms-2"
                        onClick={refreshWhatsappChatList}
                        disabled={refreshingChatList}
                        title="Refresh WhatsApp Chat List"
                      >
                        {refreshingChatList ? (
                          <span class="spinner-border spinner-border-sm" role="status"></span>
                        ) : (
                          <i class="bi bi-arrow-clockwise"></i>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
              <div class="modal-footer">
                <button 
                  type="button" 
                  class="btn btn-secondary" 
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  class="btn btn-primary" 
                  onClick={handleAddAgency}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                      Saving...
                    </>
                  ) : (
                    'Save Agency'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Agency Modal */}
      {showEditModal && (
        <div class="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div class="modal-dialog">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">Edit Agency</h5>
                <button 
                  type="button" 
                  class="btn-close" 
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                ></button>
              </div>
              <div class="modal-body">
                <form>
                  <div class="mb-3">
                    <label class="form-label">Agency Name *</label>
                    <input
                      type="text"
                      class="form-control"
                      name="agency_name"
                      value={formData.agency_name}
                      onChange={handleInputChange}
                      placeholder="Enter agency name"
                      required
                    />
                  </div>
                  <div class="mb-3">
                    <label class="form-label">Status</label>
                    <select
                      class="form-select"
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </select>
                  </div>
                  <div class="mb-3">
                    <label class="form-label">WhatsApp Chat ID</label>
                    <div class="d-flex align-items-center">
                      <select
                        class="form-select"
                        name="agent_whatsapp_chat_id"
                        value={formData.agent_whatsapp_chat_id}
                        onChange={handleInputChange}
                      >
                        <option value="">Select a WhatsApp Chat ID</option>
                        {whatsappChatList.map((chat) => (
                          <option key={chat.chat_id} value={chat.chat_id}>
                            {chat.chat_name} ({chat.chat_id})
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-secondary ms-2"
                        onClick={refreshWhatsappChatList}
                        disabled={refreshingChatList}
                        title="Refresh WhatsApp Chat List"
                      >
                        {refreshingChatList ? (
                          <span class="spinner-border spinner-border-sm" role="status"></span>
                        ) : (
                          <i class="bi bi-arrow-clockwise"></i>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
              <div class="modal-footer">
                <button 
                  type="button" 
                  class="btn btn-secondary" 
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  class="btn btn-primary" 
                  onClick={handleEditAgency}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                      Updating...
                    </>
                  ) : (
                    'Update Agency'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Merge Agency Modal */}
      {showMergeModal && selectedAgency && (
        <div class="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div class="modal-dialog modal-lg">
            <div class="modal-content">
              <div class="modal-header">
                <h5 class="modal-title">Merge Agency</h5>
                <button 
                  type="button" 
                  class="btn-close" 
                  onClick={() => {
                    setShowMergeModal(false);
                    resetMergeForm();
                    setSelectedAgency(null);
                  }}
                ></button>
              </div>
              <div class="modal-body">
                <div class="alert alert-warning" role="alert">
                  <i class="bi bi-exclamation-triangle me-2"></i>
                  <strong>Warning:</strong> This action will merge the source agency into the target agency. 
                  All bookings from the source agency will be transferred to the target agency, and the source agency will be deactivated. 
                  This action cannot be undone.
                </div>
                
                <form>
                  <div class="row">
                    <div class="col-md-6">
                      <div class="mb-3">
                        <label class="form-label">Source Agency (Will be merged)</label>
                        <input
                          type="text"
                          class="form-control"
                          value={selectedAgency.agency_name}
                          disabled
                        />
                        <small class="text-muted">This agency will be deactivated after merge</small>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="mb-3">
                        <label class="form-label">Target Agency (Will receive bookings) *</label>
                        <select
                          class="form-select"
                          name="target_agency_id"
                          value={mergeFormData.target_agency_id}
                          onChange={handleMergeFormChange}
                          required
                        >
                          <option value="">Select target agency...</option>
                          {agenciesForMerge.map((agency) => (
                            <option key={agency.agency_id} value={agency.agency_id}>
                              {agency.agency_name} {agency.chat_name && `(${agency.chat_name})`}
                            </option>
                          ))}
                        </select>
                        <small class="text-muted">This agency will receive all bookings from the source agency</small>
                      </div>
                    </div>
                  </div>
                  
                  <div class="mb-3">
                    <label class="form-label">Merge Summary</label>
                    <div class="card bg-light">
                      <div class="card-body">
                        <p class="mb-1"><strong>Source Agency:</strong> {selectedAgency.agency_name}</p>
                        <p class="mb-1"><strong>Target Agency:</strong> {
                          mergeFormData.target_agency_id ? 
                          agenciesForMerge.find(a => a.agency_id === mergeFormData.target_agency_id)?.agency_name || 'Not selected' :
                          'Not selected'
                        }</p>
                        <p class="mb-0"><strong>Action:</strong> All bookings will be transferred and source agency will be deactivated</p>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              <div class="modal-footer">
                <button 
                  type="button" 
                  class="btn btn-secondary" 
                  onClick={() => {
                    setShowMergeModal(false);
                    resetMergeForm();
                    setSelectedAgency(null);
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  class="btn btn-warning" 
                  onClick={handleMergeAgencies}
                  disabled={mergeLoading || !mergeFormData.target_agency_id}
                >
                  {mergeLoading ? (
                    <>
                      <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                      Merging...
                    </>
                  ) : (
                    <>
                      <i class="bi bi-arrow-right-circle me-2"></i>
                      Merge Agencies
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Agencies; 